import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import RoleNavigation from '../../components/ui/RoleNavigation';
import Icon from '../../components/AppIcon';
import StepIndicator from './components/StepIndicator';
import PersonalInfoStep from './components/PersonalInfoStep';
import KYCVerificationStep from './components/KYCVerificationStep';
import SocialVerificationStep from './components/SocialVerificationStep';
import ReviewSubmitStep from './components/ReviewSubmitStep';
import ApplicationStatusTracker from './components/ApplicationStatusTracker';

const TOTAL_STEPS = 4;

const CardApplicationFormScreen = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [scScore, setScScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [errors, setErrors] = useState({});

  const [personalData, setPersonalData] = useState({
    fullName: userProfile?.full_name || '',
    dob: '',
    nationality: '',
    phoneCode: '+1',
    phone: '',
    address: { street: '', city: '', state: '', postcode: '', country: '' },
    employmentStatus: '',
    incomeRange: '',
  });

  const [kycData, setKycData] = useState({
    documentType: '',
    frontUrl: '',
    backUrl: '',
    selfieUrl: '',
    documentExpiry: '',
  });

  const [socialData, setSocialData] = useState({
    socialHandle: '',
    platform: '',
    audienceSize: '',
    monthlyEarnings: '',
  });

  const loadScore = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await supabase
        ?.from('scores')
        ?.select('total_score')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();
      if (data) setScScore(data?.total_score || 0);
    } catch (err) {
      console.error('[CardApp] loadScore error:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    loadScore();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (userProfile?.full_name) {
      setPersonalData((prev) => ({ ...prev, fullName: userProfile?.full_name }));
    }
  }, [userProfile?.full_name]);

  const validateStep = (step) => {
    const errs = {};
    if (step === 1) {
      if (!personalData?.fullName?.trim()) errs.fullName = 'Full name is required';
      if (!personalData?.dob) errs.dob = 'Date of birth is required';
      if (!personalData?.nationality?.trim()) errs.nationality = 'Nationality is required';
      if (!personalData?.phone?.trim()) errs.phone = 'Phone number is required';
      if (!personalData?.address?.street?.trim()) errs.street = 'Street address is required';
      if (!personalData?.address?.city?.trim()) errs.city = 'City is required';
      if (!personalData?.address?.postcode?.trim()) errs.postcode = 'Postcode is required';
      if (!personalData?.address?.country?.trim()) errs.country = 'Country is required';
      if (!personalData?.employmentStatus) errs.employmentStatus = 'Employment status is required';
      if (!personalData?.incomeRange) errs.incomeRange = 'Income range is required';
    }
    if (step === 2) {
      if (!kycData?.documentType) errs.documentType = 'Please select a document type';
      if (!kycData?.frontUrl) errs.frontUrl = 'Front document upload is required';
      if (!kycData?.selfieUrl) errs.selfieUrl = 'Selfie upload is required';
      if (!kycData?.documentExpiry) errs.documentExpiry = 'Document expiry date is required';
    }
    if (step === 3) {
      if (!socialData?.socialHandle?.trim()) errs.socialHandle = 'Social handle is required';
      if (!socialData?.platform) errs.platform = 'Please select a platform';
      if (!socialData?.audienceSize) errs.audienceSize = 'Audience size is required';
      if (!socialData?.monthlyEarnings) errs.monthlyEarnings = 'Monthly earnings range is required';
    }
    return errs;
  };

  const handleNext = () => {
    const errs = validateStep(currentStep);
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS));
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setErrors({});
    setCurrentStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!termsAccepted || !consentAccepted) {
      setSubmitError('Please accept the terms and consent to proceed.');
      return;
    }
    setSubmitError('');
    setSubmitting(true);

    try {
      const payload = {
        user_id: user?.id,
        full_name: personalData?.fullName,
        dob: personalData?.dob,
        nationality: personalData?.nationality,
        address_json: personalData?.address,
        phone: `${personalData?.phoneCode || ''} ${personalData?.phone}`?.trim(),
        employment_status: personalData?.employmentStatus,
        income_range: personalData?.incomeRange,
        document_type: kycData?.documentType,
        document_front_url: kycData?.frontUrl,
        document_back_url: kycData?.backUrl || '',
        selfie_url: kycData?.selfieUrl,
        document_expiry: kycData?.documentExpiry,
        social_handle: socialData?.socialHandle,
        platform: socialData?.platform,
        audience_size: socialData?.audienceSize,
        monthly_earnings: socialData?.monthlyEarnings,
        sc_score: scScore,
        status: 'pending',
      };

      const { data, error } = await supabase
        ?.from('card_applications')
        ?.insert(payload)
        ?.select('id')
        ?.single();

      if (error) throw error;

      setApplicationId(data?.id);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('[CardApp] submit error:', err);
      setSubmitError(err?.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <ApplicationStatusTracker applicationId={applicationId} />;
  }

  return (
    <>
      <Helmet>
        <title>Apply for SocialCents Card — SocialCents</title>
      </Helmet>
      <RoleNavigation role={userProfile?.role || 'influencer'} userName={userProfile?.full_name || user?.email} />
      <div className="min-h-screen pt-[60px]" style={{ background: '#0F172A' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">

          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/social-cents-card-screen')}
              className="flex items-center gap-2 mb-5 text-sm transition-all hover:opacity-80"
              style={{ color: 'rgba(255,255,255,0.45)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <Icon name="ArrowLeft" size={15} />
              Back to Card Page
            </button>

            <div className="flex items-center gap-3 mb-2">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg,#0d9488,#1d4ed8)' }}
              >
                <Icon name="CreditCard" size={18} color="#fff" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Card Application</h1>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Step {currentStep} of {TOTAL_STEPS}</p>
              </div>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="mb-8">
            <StepIndicator currentStep={currentStep} />
          </div>

          {/* Form Card */}
          <div
            className="rounded-2xl p-6 sm:p-8 mb-6"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(12px)',
            }}
          >
            {currentStep === 1 && (
              <PersonalInfoStep
                data={personalData}
                onChange={setPersonalData}
                errors={errors}
              />
            )}
            {currentStep === 2 && (
              <KYCVerificationStep
                data={kycData}
                onChange={setKycData}
                errors={errors}
                userId={user?.id}
              />
            )}
            {currentStep === 3 && (
              <SocialVerificationStep
                data={socialData}
                onChange={setSocialData}
                errors={errors}
                scScore={scScore}
              />
            )}
            {currentStep === 4 && (
              <ReviewSubmitStep
                personalData={personalData}
                kycData={kycData}
                socialData={socialData}
                scScore={scScore}
                termsAccepted={termsAccepted}
                consentAccepted={consentAccepted}
                onTermsChange={setTermsAccepted}
                onConsentChange={setConsentAccepted}
                submitting={submitting}
                submitError={submitError}
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: currentStep === 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.7)',
                cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              <Icon name="ArrowLeft" size={15} />
              Back
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: TOTAL_STEPS })?.map((_, i) => (
                <div
                  key={i}
                  className="rounded-full transition-all"
                  style={{
                    width: currentStep === i + 1 ? '20px' : '6px',
                    height: '6px',
                    background: currentStep > i + 1
                      ? '#14B8A6'
                      : currentStep === i + 1
                      ? 'linear-gradient(90deg,#0d9488,#1d4ed8)'
                      : 'rgba(255,255,255,0.15)',
                  }}
                />
              ))}
            </div>

            {currentStep < TOTAL_STEPS ? (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg,#0d9488,#1d4ed8)', cursor: 'pointer' }}
              >
                Continue
                <Icon name="ArrowRight" size={15} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{
                  background: submitting ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg,#0d9488,#1d4ed8)',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} />
                    Submitting…
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={15} />
                    Submit Application
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CardApplicationFormScreen;
